// libs
import _ from "lodash";
import { oc } from "ts-optchain";

// vue
import Vue from "vue";
import { mapGetters } from "vuex";

// Icons
import { faChevronLeft, faList, faPlus, faDownload, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// services
import {
  ErrorLogService,
  IAddPartListBuilderRequest,
  PartListBuilderService,
  PartListService,
  QuickOrderService,
  CurrencyService,
} from "@/services";

//mixins 
import {
  stringHelper
} from "@/components/mixins";

// components
import EnterPartTab from "./Tabs/EnterPart/EnterPartTab.vue";
import PartListsTab from "./Tabs/PartLists/PartListsTab.vue";
import UploadFileTab from "./Tabs/UploadFile/UploadFileTab.vue";

import AddToCartDialog from "./Dialogs/AddToCartDialog/AddToCartDialog.vue";
import AddToListDialog from "./Dialogs/AddToListDialog/AddToListDialog.vue";
import DeleteListConfirmation from "./Dialogs/DeleteListConfirmation/DeleteListConfirmation.vue";
import RemovePartsConfirmation from "./Dialogs/RemovePartsConfirmation/RemovePartsConfirmation.vue";

import { PartsListGrid } from "@/components/Elements/PartsListGrid";
import { DownloadJsonToCsv } from "@/components/Elements/DownloadJsonToCsv"

// types
import { IPartListBuilderResponseDto, IUserInfo, PartValidationReason } from "@/types";
import { IQuickOrdersContext } from "./QuickOrders.types";

export default Vue.extend({
  name: "QuickOrders",
  components: {
    FontAwesomeIcon,
    EnterPartTab,
    UploadFileTab,
    PartListsTab,
    AddToCartDialog,
    AddToListDialog,
    DeleteListConfirmation,
    RemovePartsConfirmation,
    PartsListGrid,
    DownloadJsonToCsv,
  },
  mixins: [stringHelper],
  props: {
    id: Number || String,
  },
  computed: {
    ...mapGetters(["activeAccountId", "user", "getContentItem", "audienceIds", "canSeeOemList", "billingCurrencyCode", "canPlaceOrders", "canSeeContractPrice", "canViewQuickOrder", "tenantDetails", "McfCostCenterId"]),
    context() {
      return {
        accountId: this.activeAccountId,
        user: this.user,
        partListId: this.partListId,
        userAudiences: this.audienceIds,
        showLocation: this.showLocation,
        showDealerSku: this.showDealerSku,
        showOEMPricing: this.showOEMPricing,
      };
    },

    partListId() {
      return oc(this.partListData as IPartListBuilderResponseDto).Id(0);
    },

    isPartListType() {
      return oc(this.partListData as IPartListBuilderResponseDto).ListType() === 'PartList';
    },

    anyItemsSelected() {      
      if (this.partListData && this.partListData.Items) {
        return this.partListData.Items.some((x: IGridListRowItem) => x.Selected);
      }

      return false;
    },
    disableAddToCart(){
      let selectedItems = this.partListData.Items.filter(item => item.Selected);
      let allPartsValid = this.checkPartsAreValid(selectedItems);
      let zeroPrices = this.partListData.Items.some((x: any) => x.UnitPrice == 0);

      return !this.anyItemsSelected || !allPartsValid || zeroPrices;
    },

    showValidationResults() {
      return oc(this.partListData as IPartListBuilderResponseDto).ValidationResults.ValidationErrors([]).length > 0;
    },
    
    isCustomerSku() {
      return this.user.IsCustomerSku;
    },

    itemsToExportToExcel(){
      let selectedItems = this.partListData.Items.filter( x => x.Selected === true)      
    
      let mappedItems = selectedItems.map(item => {

        // PAS-8409 check user input of StockingLocation/DealerSku for quote marks and special chars, then remove them since it breaks the csv
        if (item.DealerSku) {
          item.DealerSku = item.DealerSku.replace(/[^a-zA-Z0-9 ]/g, '');
        }

        if (item.StockingLocation) {
          item.StockingLocation = item.StockingLocation.replace(/[^a-zA-Z0-9 ]/g, '');
        }

        let mappedItem = {
          DealerNetPartNumber: item.McfPartCode,
          Quantity: item.Quantity,
          Oem: item.OemDescription,
          OemPartNumber: item.OemPartCode,
          PartDescription: item.PartDescription,
          DealerBinLocation: this.IsNullOrUndefined( item.StockingLocation) ? "" : item.StockingLocation,
          DealerSku: this.IsNullOrUndefined( item.DealerSku) ? "" : item.DealerSku,
          EmergencyFeePercentage: item.EmergencyFeePercentage,
          Weight: item.Weight,
        };

        let fleetListTitle = `FleetListPrice (${this.billingCurrencyCode})`;
        let coreCostTitle = `CoreCost (${this.billingCurrencyCode})`;
        let unitPriceTitle = `UnitPrice (${this.billingCurrencyCode})`;
        let totalCostTitle = `TotalCost (${this.billingCurrencyCode})`;
        let nprCommentsTitle = `Note`;

        // If not MLAP
        if(this.McfCostCenterId !== 3) {
          mappedItem[fleetListTitle] = item.FleetListPrice;
          mappedItem[coreCostTitle] = item.CoreCost;
          mappedItem[unitPriceTitle] = item.UnitPrice;
          mappedItem[totalCostTitle] = item.TotalPrice;
        }

        // If MLAP and frozen or discontinued
        else if(["IsSoftFrozen","IsHardFrozen","IsDiscontinued"].includes(item.PartValidation.ValidationReason)){
          // If JPY Currency code then round to nearest whole number
          this.billingCurrencyCode === "JPY" ? mappedItem[coreCostTitle] = Math.round(item.CoreCost) : mappedItem[coreCostTitle] = item.CoreCost;

          // Leave unit and total price blank if the item is frozen or discontinued
          mappedItem[unitPriceTitle] = "";
          mappedItem[totalCostTitle] = "";
          mappedItem[coreCostTitle] = "";
          mappedItem["EmergencyFeePercentage"] = "";
        }

        // If MLAP and can see pricing, not frozen, and not discontinued
        else { 
          mappedItem[nprCommentsTitle] = item.NPRComments;
          if(!this.canSeeContractPrice)     {
            mappedItem[coreCostTitle] = "";
            mappedItem[unitPriceTitle] = "";
            mappedItem[totalCostTitle] = "";
            mappedItem["EmergencyFeePercentage"] = "";
          }
          // If JPY Currency code then round to nearest whole number
          else if(this.billingCurrencyCode === "JPY"){
            mappedItem[coreCostTitle] = Math.round(item.CoreCost);
            mappedItem[unitPriceTitle] = Math.round(item.UnitPrice);
            mappedItem[totalCostTitle] = Math.round(item.TotalPrice);
          }
          else{
            mappedItem[coreCostTitle] = item.CoreCost;
            mappedItem[unitPriceTitle] = item.UnitPrice;
            mappedItem[totalCostTitle] = item.TotalPrice;
          }
        }

        // add warehouse stock information
        for(let i = 0; i < item.WarehouseStockQuantities.length; i++){
          let warehouseStock = item.WarehouseStockQuantities[i];
          // If MLAP only show SI stock
          if(this.McfCostCenterId !== 3 || warehouseStock.WarehouseCode === "SI") {
            let warehouseName = `Warehouse-Quantity (${warehouseStock.WarehouseCode})`;
            mappedItem[warehouseName] = warehouseStock.Quantity;
          }
        }

        // add warehouse tariff code information for the facing warehouse; it's always the first in the list
        if( item.WarehouseStockQuantities.length > 0){
          let warehouseStock = item.WarehouseStockQuantities[0];
        
          let tariffCodeName = `Facing-Warehouse-TariffCode (${warehouseStock.WarehouseCode})`;
          mappedItem[tariffCodeName] = warehouseStock.TariffCode;
        }

        return mappedItem;
      });
      
      return mappedItems
    },
    invalidParts() {
      var parts = this.partListData.Items.map(
      (part: any, i: number, isUpdating: Boolean, isUpdatingQty: Boolean) =>
        ({ ...part, index: i + 1, isUpdating: false, isUpdatingQty: false })
      );
      return parts.filter(function(x)
      {
        return !x.PartValidation.IsValid;
      })
     }
  },
  created() {
    // When dealing with partLists, dialogs are a little different, this boolean set off of query string helps. 
    let self = this;
    Object.keys(this.$route.query).forEach(function (prop) {
      if (prop === "partListId") {
        self.isPartList = true;
      }
    });
  },
  data: () => ({
    backIcon: faChevronLeft,
    plusIcon: faPlus,
    listIcon: faList,
    exportExcelIcon: faFileExcel,
    downloadIcon: faDownload,

    activeView: "DEFAULT",
    activeTabIndex: 0,
    isMounted: false,
    isLoadingPartList: false,

    partListData: undefined,

    showLocation: false,
    showDealerSku: false,
    showOEMPricing: false,

    newListId: 0,
    newListName: "",
    removeItemsFromThisList: false,

    isPartList: false,
    removeHyphenSearch: false,

    shouldClearQuickOrder: false,
    failedToLoad: false,
  }),
  methods: {
    async reloadList() {
      this.activeTabIndex = 0;

      return this.loadPartList(this.partListId);
    },

    async clearActivePartLists(){
      try {
        const params = {
          accountId: this.activeAccountId,
          userId: this.user.UserId
        };
        await PartListService.ClearActivePartLists(params);
      } finally {
        this.failedToLoad = false;
      }
    },

    async loadPartList(partListId?: string | number) {
      try {

        this.isLoadingPartList = true;

        const user = this.user as IUserInfo;

        const req = {
          accountId: this.activeAccountId,
          userId: user.UserId,
          partListId: partListId ? Number(partListId) : 0,
          userAudiences: this.audienceIds,
          removeHyphenSearch: false
        };

        const data = await PartListBuilderService.GetPartList(req);
        this.setPartListData(data);
      } catch (err) {
        ErrorLogService.LogException(err);
        this.failedToLoad = true;
      } finally {
        this.isLoadingPartList = false;
      }
    },

    renameList: _.debounce(async function(listName: string) {
      try {
        const context = this.context as IQuickOrdersContext;

        const rq = {
          accountId: context.accountId,
          userId: context.user.UserId,
          partListId: Number(context.partListId),
          newName: listName,
        };

        await PartListService.RenameList(rq);

        this.showMessage("List renamed");

      } catch (err) {
        this.showError(err);
      }
    }, 1500),

    setPartListData(data: IPartListBuilderResponseDto) {
      if (data) {

        data.Items.forEach((element: any) => {
          const item = element;

          item.newStockingLocation = item.StockingLocation;
          item.newDealerSku = item.DealerSku;

          if (item.PartValidation && item.PartValidation.ValidationReason !== PartValidationReason.IsValid) {
            item._showDetails = true;
          }
          
          if (!item.CanShipAir){
           item._showDetails = true;
          }

          if (item.PartDescription && item.PartDescription.endsWith("Long") && item.PartDescription.indexOf(" ") === -1) {
            item.PartDescription = this.getContentItem(item.PartDescription);
          }
        });
      }

      this.partListData = data;
    },

    async addPartToList(params: IAddPartListBuilderRequest) {

      try {
        if (params.partListId === 0) {
          params.partListId = this.partListId;
        }
        const data = await PartListBuilderService.AddItemToList(params);

        if( data === true){
          this.showMessage(`Part ${params.oemPartCode} added`);
        }        

        this.loadPartList(this.partListId);
      } catch (err) {
        this.showError(err);
      }
    },

    async addCrossPart(params) {
      try {
        params.addRequest.partListId = this.partListId;
        params.deleteRequest.partListId = this.partListId;

        const data = await PartListBuilderService.AddCrossReference(params);

        this.partListData = data;

        this.showMessage(`Part ${params.originalOemPartCode} added`);

        this.loadPartList(this.partListId);
      } catch (err) {
        this.showError(err);
      }
    },

    async rowUpdated(row: any) {

      const user = this.user as IUserInfo;

      const rq = {
        accountId: this.activeAccountId,
        userId: user.UserId,
        partListId: this.partListId,
        userAudiences: this.audienceIds,
        itemId: row.Id,
        item: {
          itemId: row.Id,
          dealerPartNumber: row.DealerSku,
          oemPartCode: row.OemPartCode,
          quantity: row.Quantity,
          stockingLocation: row.StockingLocation,
          oemId: row.Oem.Id,
        },
      };

      const response = await PartListBuilderService.UpdateListItem(rq);

      const responseItems = oc(response).Items([]);

      this.partListData.Items = responseItems.map((item) => {
        const currentItem = this.partListData.Items.find((x: IGridListRowItem) => x.Id === row.Id);

        if(currentItem.Id == item.Id) {
          currentItem.newStockingLocation = row.newStockingLocation;
          currentItem.newDealerSku = row.newDealerSku;
        } else {
          currentItem.newStockingLocation = item.StockingLocation;
          currentItem.newDealerSku = item.DealerSku;
        }

        return {
          ...currentItem,
          ...item,
        };
      });
    },

    rowSelectionUpdated(row: IGridListRowItem) {
      this.partListData.Items = this.partListData.Items.map((item: IGridListRowItem) => {
        if (item.Id === row.Id) {
          return {
            ...item,
            ...row,
          };
        }
        return { ...item };
      });
    },

    onItemsRemoved(data: IPartListBuilderResponseDto) {
      try {
        this.setPartListData(data);

        this.showMessage("Items removed");
      } finally {
        this.activeView = "DEFAULT";
      }
    },

    onItemsAddedToList(result: any) {
      try {
        this.showMessage("Items added to list");        

      } finally {
        this.activeView = "DEFAULT";

        if( this.shouldClearQuickOrder){
          this.showRemovePartsConfirmation();
        }
      }
    },

    onItemsAddedToCart(result: any) {
      try {
        this.showMessage("Items added to cart");
      } finally {
        this.activeView = "DEFAULT";
      }
    },

    async addSelectedItemsToList(goToNewList: boolean) {
      const user = this.user as IUserInfo;
      const items = this.partListData.Items as IGridListRowItem[];

      const rq = {
        accountId: this.activeAccountId,
        userId: user.UserId,
        sourceListId: Number(this.partListId),
        sourceItemIds: items.filter((x) => x.Selected).map((x) => x.Id),
        removeImportedItemsFromSource: this.removeItemsFromThisList,
        newListId: this.newListId,
        newListName: this.newListName,
      };

      const result = await QuickOrderService.addItemsToList(rq);

      if (goToNewList) {
        this.$router.push({ name: "QuickOrders", query: { partListId: result.partListId } });
      } else {
        this.reloadList();
      }
    },

    reloadQuickOrder() {
      this.loadPartList(null);
    },

    goToQuickOrders() {
      this.$router.push({ name: "QuickOrders" });
    },

    showAddToCartDialog() {
      this.activeView = "ADD_TO_CART";
    },

    showAddToListDialog() {
      this.activeView = "ADD_TO_LIST";
    },

    showRemovePartsConfirmation() {
      this.activeView = "REMOVE_PARTS";
      this.shouldClearQuickOrder = false;
    },

    showDeleteListConfirmation() {
      this.activeView = "DELETE_LIST";
    },

    showDefaultView() {
      this.activeView = "DEFAULT";
      if( this.shouldClearQuickOrder){
        this.showRemovePartsConfirmation();
      }
    },

    showMessage(msg: string) {
      this.$store.commit("pushTimedMessage", {
        header: msg,
        IsError: false,
        seconds: 6,
      });
    },

    showError(err: any) {
      const msg = ErrorLogService.LogAndGetErrorMessage(err);

      this.$store.commit("pushTimedMessage", {
        header: msg,
        IsError: true,
        seconds: 15,
      });
    },

    saveAndDownload(){
      this.$refs.downloadToCsv.DownloadCsv();

      this.shouldClearQuickOrder = true;
      this.$refs.addToPartList.click();
    },
    
    async replaceWithSupersession(partDetail: any) {
      this.isLoadingPartList = true;

      const partListId = this.partListId;
      const userAudiences = this.audienceIds;
      const accountId = this.activeAccountId;
      const userId = this.user.UserId;
      
      try {
        const paramsToSave = {
          accountId,
          userId,
          partListId,
          userAudiences,
          dealerPartNumber: partDetail.DealerSku,
          oemId: partDetail.Oem?.Id,
          oemPartCode: partDetail.StatusMessage.ReplacementPartOemPartCode,
          quantity: partDetail.Quantity,
          stockingLocation: partDetail.stockingLocation
        } as IAddPartListBuilderRequest;

        await PartListBuilderService.AddItemToList(paramsToSave);

      } catch (err) {
        ErrorLogService.LogException(err);

        this.$store.commit("pushTimedMessage", {
          header: "Failed to add the supersession.",
          message: "Please check your Quick Order and try again. If this problem continues, please contact support",
          IsError: true,
          seconds: 20,
        });
      }

      try {
        const paramsToDelete = {
          accountId,
          userId,
          partListId,
          userAudiences,
          itemIds: [partDetail.Id]
        };

        const result = await PartListBuilderService.DeleteItems(paramsToDelete);

        this.partListData = result;
      } catch (err) {
        ErrorLogService.LogException(err);

        this.$store.commit("pushTimedMessage", {
          header: "Failed to remove the superseded part.",
          message: "Please check your Quick Order and try again. If this problem continues, please contact support",
          IsError: true,
          seconds: 20,
        });
      } finally {
        this.showMessage(`Part ${partDetail.oemPartCode} added`);

        this.loadPartList(partListId)
      }
    },

    goToError(id){
      document.getElementById(id).scrollIntoView();
    },

    getPartValidationMessageHtml(messageKey: string, minimumQty: number) {
      if (["IsSoftFrozenLong", "IsMaoLimitExceededLong", "ThisIsAnInvalidPartButThereIsOneOrMoreCrossReferences", "ThisItemHasBeenDiscontinuedCall", "MaoSoftFrozenLong"].includes(messageKey)) {
        return this.format(this.getContentItem(messageKey), this.tenantDetails.SupportPhoneNumber);
      }
      if(messageKey === "MinimumQuantityNotMet"){
        return this.format(this.getContentItem(messageKey)) + minimumQty;
      }
      return this.getContentItem(messageKey);
    },

    checkPartsAreValid(items: any) {
      let allPartsValid = true;

      for (let i = 0; i < items.length; i++){
        if(items[i].PartValidation.IsValid === false && items[i].PartValidation.ValidationReason === "IsMao") {
          continue;
        }
        
        if (items[i].PartValidation.IsValid === false) {
          allPartsValid = false;
        }
      }

      return allPartsValid;
    }
  },
  watch: {
    showLocation() {
      if (this.isMounted) {
        QuickOrderService.setShowDealerBinPref(this.showLocation);
      }
    },
    showDealerSku() {
      if (this.isMounted) {
        QuickOrderService.setShowDealerSkuPref(this.showDealerSku);
      }
    },
    showOEMPricing() {
      if (this.isMounted) {
        QuickOrderService.setShowOemPricePref(this.showOEMPricing);
      }
    },
  },
  mounted() {
    try {
      this.accountId = this.activeAccountId;
      this.userAudiences = this.audienceIds;
      this.showLocation = QuickOrderService.getShowDealerBinPref();
      this.showDealerSku = this.isCustomerSku ? this.isCustomerSku : QuickOrderService.getShowDealerSkuPref();
      this.showOEMPricing = this.canSeeOemList && QuickOrderService.getShowOemPricePref();
      this.loadPartList(this.id);
    } finally {
      this.isMounted = true;
    }

    _.defer(() => {
      const initialView = oc(this.$route.query as any).activeView();

      if (initialView === "partLists") {
        this.activeTabIndex = 2;
      }
    });
  },
});
